import React, { FunctionComponent, useCallback, useContext, useEffect, useState } from 'react'
import debounce from 'lodash/debounce'

import { CanadianPostalCode, CanadianPostalCodeDtoFactory, CanadianPostalCodeEntry } from '@models/CanadianPostalCode'
import { CanadianPostalCodeInputProps } from '@components/AnswerInput/AnswerProps'
import { ConsentContext } from '@context/ConsentContext'
import FormField from '@components/FormField/FormField'
import { useTranslation } from 'react-i18next'
import Button from '@components/Button/Button'
import API from '@api/client'

import './CanadianPostalCodeInput.scss'

const sortPostalCodes = (a: Partial<CanadianPostalCode>, b: Partial<CanadianPostalCode>) => {
  const aNmCirc = a.id_riding!
  const bNmCirc = b.id_riding!

  if (aNmCirc < bNmCirc) {
    return -1
  }

  if (aNmCirc > bNmCirc) {
    return 1
  }

  return 0
}

const groupResults = (input: CanadianPostalCode[]) =>
  (input || []).reduce((acc, { id_riding, postal_code_can, district_name_en, district_name_fr }) => {
    acc[id_riding] = acc[id_riding] ?? { postal_code_can, id_riding, district_name_en, district_name_fr }
    return acc
  }, {} as Record<string, Partial<CanadianPostalCode>>)

const randomize = (input: any[]): Partial<CanadianPostalCode[]> => {
  let currentIndex = input.length,
    randomIndex

  while (currentIndex != 0) {
    randomIndex = Math.floor(Math.random() * currentIndex)
    currentIndex--
    ;[input[currentIndex], input[randomIndex]] = [input[randomIndex], input[currentIndex]]
  }

  return input
}

export const CanadianPostalCodeInput: FunctionComponent<CanadianPostalCodeInputProps> = ({
  question,
  onSelection,
  answer,
}) => {
  const [randomIndex, setRandomIndex] = useState(answer ? 0 : -1)
  const [choices, setChoices] = useState((answer ? [answer] : []) as any)
  const [selectedChoice, setSelectedChoice] = useState(answer as CanadianPostalCodeEntry | undefined)
  const [timer, setTimer] = useState<ReturnType<typeof setTimeout> | number>(0)
  const { hasAutoFill } = useContext(ConsentContext)

  const { t, i18n } = useTranslation('Common')

  const currentLocale = (i18n.language ?? 'fr').substring(0, 2)

  const debounceCallback = useCallback(
    debounce(async (value: string) => {
      if (typeof value !== 'undefined') {
        setSelectedChoice(undefined)
        setRandomIndex(-1)

        const cleanValue = value.replace(/_/g, '')
        const canadianPostalCodeApiResponse = (await API.searchFederalElectionDistricts(cleanValue))?.data
        const canadianPostalCodeFromDto = canadianPostalCodeApiResponse.map((code) =>
          CanadianPostalCodeDtoFactory.fromDto(code),
        )
        const resultsGrouped = groupResults(canadianPostalCodeFromDto)

        const results = randomize(Object.values(resultsGrouped)?.slice(0, 6).sort(sortPostalCodes))

        setChoices(results)

        const randomChoiceIndex = Math.floor(Math.random() * choices.length)

        setRandomIndex(randomChoiceIndex)
        onSelection(results[randomChoiceIndex] as CanadianPostalCodeEntry)
      }
    }, 1000),
    [timer, setTimer],
  )

  useEffect(() => {
    if (hasAutoFill) return

    if (question.optional && !selectedChoice) {
      handleTextChange('')
    }
  }, [])

  const handleTextChange = (value: string) => {
    if (!value || value === '___') {
      setSelectedChoice(undefined)
      setChoices([])
    }

    debounceCallback(value?.trim())
  }

  const randomClassName = (idx: number) => (idx === randomIndex ? 'random' : '')

  const handleChoiceSelect = (value: Partial<CanadianPostalCode> | undefined) => {
    const getSelectedChoice = choices.find((item: CanadianPostalCode) => item.id_riding === value?.id_riding)

    setSelectedChoice(getSelectedChoice)
    setRandomIndex(-1)
    onSelection(value as CanadianPostalCodeEntry)
  }

  const selectedClassName = (item: CanadianPostalCode | undefined) =>
    selectedChoice?.id_riding === item?.id_riding ? 'selected' : ''

  const getCanadianPostalCodeDistrictNameByRidingId = (id?: number) => {
    const selectedChoice = choices.find((item: CanadianPostalCode) => item.id_riding === id)
    const selectedChoiceByLanguage =
      currentLocale === 'fr' ? selectedChoice.district_name_fr : selectedChoice.district_name_en

    return selectedChoice ? selectedChoiceByLanguage : ''
  }

  return (
    <div className="CanadianPostalCodeInput">
      <FormField
        value={(answer as Partial<CanadianPostalCode>)?.postal_code_can}
        mask={question.meta?.inputMask}
        placeholder={question.hint}
        onChange={handleTextChange}
      />
      <div className="results">
        <div className="info">
          <p>
            {t('circonscription')} {!selectedChoice && t('random')}
          </p>
        </div>
        <div className="list">
          {choices.map((item: CanadianPostalCode | undefined, index: number) => (
            <Button
              propClassName={`${randomClassName(index)} ${selectedClassName(item)}`}
              key={item?.id_riding}
              onClick={() => handleChoiceSelect(item)}>
              {getCanadianPostalCodeDistrictNameByRidingId(item?.id_riding)}
            </Button>
          ))}
        </div>
      </div>
    </div>
  )
}
